// src/components/search/user-search.tsx
import React, { useState, useEffect } from 'react';
import { TextField, Autocomplete, Avatar, Typography, Paper, Button, Box, Chip } from '@mui/material';
import { UserPlus, UserCheck, Search, Users, Hash, BookOpen, Calendar, Store, Music2, Hotel, Building2, FileText, UtensilsCrossed, ChefHat } from 'lucide-react';
import { useSearch } from '@/hooks/useSearch';
import type { SearchResult as FirebaseSearchResult } from '@/lib/firebase/search';
import { useFriends } from '@/hooks/useFriends';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from 'react-i18next';

type SearchType = FirebaseSearchResult['type'] | 'all';

type SearchResult = FirebaseSearchResult;

export function UserSearch() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('all');
  const debouncedQuery = useDebounce(query, 300);
  const {
    searchContent,
    loading,
    results,
    effectiveType,
    parsedQuery,
    recentSearches,
    topCategories,
    clearRecentSearches,
    trackResultInteraction,
  } = useSearch();
  const { friends, sendFriendRequest } = useFriends();
  const navigate = useNavigate();

  const searchTypes = [
    { type: 'all', label: 'All', icon: Search },
    { type: 'users', label: 'People', icon: Users },
    { type: 'groups', label: 'Groups', icon: Users },
    { type: 'hotels', label: 'Hotels', icon: Hotel },
    { type: 'apartments', label: 'Apartments', icon: Building2 },
    { type: 'marketplace', label: 'Shop', icon: Store },
    { type: 'music', label: 'Music', icon: Music2 },
    { type: 'events', label: 'Events', icon: Calendar },
    { type: 'restaurants', label: 'Food', icon: UtensilsCrossed },
    { type: 'recipes', label: 'Recipes', icon: ChefHat },
    { type: 'pages', label: 'Pages', icon: FileText }
  ] as const;

  useEffect(() => {
    if (debouncedQuery.trim().length >= 2) {
      searchContent(debouncedQuery, searchType);
    }
  }, [debouncedQuery, searchType]);

  const handleSelection = (result: SearchResult) => {
    trackResultInteraction(result);
    // Clear the input and navigate
    setQuery('');
    setInputValue('');
    navigate(result.url, { replace: false });
  };

  const handleRecentSearchClick = (nextQuery: string, nextType: SearchType) => {
    setSearchType(nextType);
    setInputValue(nextQuery);
    setQuery(nextQuery);
    searchContent(nextQuery, nextType);
  };

  const shouldShowRecentSearches = query.trim().length < 2 && recentSearches.length > 0;

  const getCategoryColor = (category?: string) => {
    const colors: Record<string, string> = {
      'People': 'primary',
      'Groups': 'success',
      'Hotels': 'info',
      'Apartments': 'info',
      'Marketplace': 'warning',
      'Music': 'secondary',
      'Events': 'error',
      'Pages': 'default',
      'Restaurants': 'warning',
      'Recipes': 'success'
    };
    return colors[category || ''] || 'default';
  };

  const renderSearchResult = (result: SearchResult) => {
    const score = typeof result.relevanceScore === 'number' ? result.relevanceScore : 0;
    const confidenceLabel = score >= 130 ? 'Top match' : score >= 95 ? 'Strong' : score >= 60 ? 'Related' : null;

    switch (result.type) {
      case 'users':
        return (
          <Box className="flex items-center justify-between w-full p-2">
            <div className="flex items-center gap-3 flex-1">
              <Avatar
                src={result.image}
                alt={result.title}
                sx={{ width: 40, height: 40 }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Typography variant="subtitle1" className="truncate">{result.title}</Typography>
                  {confidenceLabel && (
                    <Chip
                      label={confidenceLabel}
                      size="small"
                      color="secondary"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {result.category && (
                    <Chip 
                      label={result.category} 
                      size="small" 
                      color={getCategoryColor(result.category) as any}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </div>
                <Typography variant="body2" color="text.secondary" className="truncate">
                  {result.subtitle}
                </Typography>
              </div>
            </div>
            {!friends.some(f => f.participants.includes(result.id)) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<UserPlus className="w-3 h-3" />}
                onClick={(e) => {
                  e.stopPropagation();
                  sendFriendRequest(result.id);
                }}
                sx={{ borderRadius: '20px', ml: 2 }}
              >
                Add
              </Button>
            )}
          </Box>
        );

      case 'hotels':
      case 'apartments':
        return (
          <Box className="flex items-center gap-3 p-2">
            {result.image ? (
              <img
                src={result.image}
                alt={result.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                {result.type === 'hotels' ? <Hotel className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Typography variant="subtitle1" className="truncate">{result.title}</Typography>
                {confidenceLabel && (
                  <Chip
                    label={confidenceLabel}
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {result.category && (
                  <Chip 
                    label={result.category} 
                    size="small" 
                    color={getCategoryColor(result.category) as any}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </div>
              {result.subtitle && (
                <Typography variant="body2" color="text.secondary" className="truncate">
                  {result.subtitle}
                </Typography>
              )}
            </div>
          </Box>
        );

      case 'marketplace':
        return (
          <Box className="flex items-center gap-3 p-2">
            {result.image ? (
              <img
                src={result.image}
                alt={result.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center">
                <Store className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Typography variant="subtitle1" className="truncate font-semibold">{result.title}</Typography>
                {confidenceLabel && (
                  <Chip
                    label={confidenceLabel}
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {result.category && (
                  <Chip 
                    label={result.category} 
                    size="small" 
                    color={getCategoryColor(result.category) as any}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </div>
              {result.subtitle && (
                <Typography variant="body2" color="text.secondary" className="truncate">
                  {result.subtitle}
                </Typography>
              )}
            </div>
          </Box>
        );

      case 'music':
        return (
          <Box className="flex items-center gap-3 p-2">
            {result.image ? (
              <img
                src={result.image}
                alt={result.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <Music2 className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Typography variant="subtitle1" className="truncate">{result.title}</Typography>
                {confidenceLabel && (
                  <Chip
                    label={confidenceLabel}
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {result.category && (
                  <Chip 
                    label={result.category} 
                    size="small" 
                    color={getCategoryColor(result.category) as any}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </div>
              {result.subtitle && (
                <Typography variant="body2" color="text.secondary" className="truncate">
                  {result.subtitle}
                </Typography>
              )}
            </div>
          </Box>
        );

      default:
        return (
          <Box className="flex items-center gap-3 p-2">
            {result.image ? (
              <img
                src={result.image}
                alt={result.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                <Search className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Typography variant="subtitle1" className="truncate">{result.title}</Typography>
                {confidenceLabel && (
                  <Chip
                    label={confidenceLabel}
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {result.category && (
                  <Chip 
                    label={result.category} 
                    size="small" 
                    color={getCategoryColor(result.category) as any}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </div>
              {result.subtitle && (
                <Typography variant="body2" color="text.secondary" className="truncate">
                  {result.subtitle}
                </Typography>
              )}
            </div>
          </Box>
        );
    }
  };

  return (
    <div className="w-full max-w-2xl">
      <Autocomplete
        freeSolo
        options={results}
        filterOptions={(x) => x}
        getOptionLabel={(option) => 
          typeof option === 'string' ? option : option.title
        }
        inputValue={inputValue}
        onInputChange={(_, value) => {
          setInputValue(value);
          setQuery(value);
        }}
        onChange={(_, value) => {
          if (value && typeof value !== 'string') {
            handleSelection(value);
          }
        }}
        loading={loading}
        loadingText={t('common.loading')}
        noOptionsText={
          query.length < 2 
            ? 'Type at least 2 characters to search...'
            : `No results found for "${query}"`
        }
        clearOnBlur={false}
        selectOnFocus={false}
        PaperComponent={({ children }) => (
          <Paper elevation={4} className="mt-1 rounded-lg">
            <div className="p-2 border-b">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {searchTypes.map(({ type, label, icon: Icon }) => (
                  <Button
                    key={type}
                    variant={searchType === type ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<Icon className="w-4 h-4" />}
                    onClick={() => setSearchType(type)}
                    sx={{ 
                      borderRadius: '20px', 
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      px: 2
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {shouldShowRecentSearches && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <Typography variant="caption" className="text-gray-600 font-medium">
                      Recent searches
                    </Typography>
                    <Button
                      size="small"
                      onClick={clearRecentSearches}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                    >
                      Clear
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {recentSearches.slice(0, 6).map((entry) => {
                      const typeMeta = searchTypes.find((item) => item.type === entry.type);
                      const Icon = typeMeta?.icon || Search;
                      return (
                        <Chip
                          key={`${entry.type}-${entry.query}`}
                          icon={<Icon className="w-3 h-3" />}
                          label={`${entry.query} ${entry.type !== 'all' ? `(${entry.type})` : ''}`}
                          size="small"
                          variant="outlined"
                          onClick={() => handleRecentSearchClick(entry.query, entry.type)}
                          sx={{ fontSize: '0.72rem', maxWidth: 220 }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {query.length >= 2 && results.length > 0 && (
              <div className="px-3 py-2 bg-gray-50 text-xs text-gray-600 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span>Found {results.length} result{results.length !== 1 ? 's' : ''}</span>
                  {effectiveType !== searchType && (
                    <Chip
                      label={`Intent: ${effectiveType}`}
                      size="small"
                      color="primary"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                  {parsedQuery && parsedQuery !== query.trim().toLowerCase() && (
                    <Chip
                      label={`Query normalized`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {topCategories.map((entry) => (
                    <Chip
                      key={entry.category}
                      label={`${entry.category}: ${entry.count}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  ))}
                </div>
              </div>
            )}
            {children}
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search people, hotels, products, music, and more..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <Search className="w-5 h-5 text-gray-500 mr-2" />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{ 
              '&:hover': { 
                backgroundColor: 'action.hover',
                cursor: 'pointer'
              },
              borderBottom: '1px solid rgba(0,0,0,0.05)'
            }}
            onClick={() => handleSelection(option)}
          >
            {renderSearchResult(option)}
          </Box>
        )}
      />
    </div>
  );
}
